import { useMemo } from 'react';
import { Radio, Select, Typography, Spin, Empty, Divider, Space } from 'antd';
import { getFieldIcon } from '../../../components/Forecast/icons';
import { useEventSelection } from '../../../contexts/useEventSelection';
import { useObservationFields } from '../../../hooks/useObservationFields';
import { useStationObservations } from '../useStationObservations';
import type { WidgetSettingsProps } from './types';

const { Text } = Typography;

export const StationParameterCardSettings = ({
  config,
  onChange,
}: WidgetSettingsProps) => {
  const { selectedEvent } = useEventSelection();
  const { stations, loading } = useStationObservations();
  const { getVisibleFields, loading: fieldsLoading } = useObservationFields();
  const isDemo = selectedEvent?.demo ?? false;

  const selectedStation = (config.selectedStation as string | undefined) ?? 'avg';
  const fieldName = (config.fieldName as string | undefined) ?? 'temperature';

  const availableStations = useMemo(
    () => stations.map((s) => s.station_reference),
    [stations],
  );

  const availableFieldNames = useMemo(() => {
    if (selectedStation === 'avg') {
      const names = new Set<string>();
      for (const station of stations) {
        for (const cfg of station.reading_config) {
          names.add(cfg.name);
        }
      }
      return Array.from(names);
    }
    const station = stations.find((s) => s.station_reference === selectedStation);
    return station?.reading_config.map((cfg) => cfg.name) ?? [];
  }, [stations, selectedStation]);

  const fieldOptions = useMemo(() => {
    return getVisibleFields(isDemo)
      .filter((field) => availableFieldNames.includes(field.name))
      .sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999))
      .map((field) => ({
        value: field.name,
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {getFieldIcon(field.name, { size: 14 })}
            {field.display_name}
          </span>
        ),
      }));
  }, [getVisibleFields, isDemo, availableFieldNames]);

  const handleStationChange = (station: string) => {
    const nextFieldNames =
      station === 'avg'
        ? Array.from(
            new Set(stations.flatMap((s) => s.reading_config.map((cfg) => cfg.name))),
          )
        : (stations.find((s) => s.station_reference === station)?.reading_config.map(
            (cfg) => cfg.name,
          ) ?? []);

    const visibleNames = getVisibleFields(isDemo)
      .filter((f) => nextFieldNames.includes(f.name))
      .map((f) => f.name);

    const updates: Record<string, unknown> = { selectedStation: station };
    if (fieldName && !visibleNames.includes(fieldName) && visibleNames.length > 0) {
      updates.fieldName = visibleNames[0];
    }
    onChange(updates);
  };

  const handleFieldChange = (name: string) => {
    onChange({ fieldName: name });
  };

  const renderStationSelection = () => {
    if (!selectedEvent) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select an event to configure stations"
        />
      );
    }

    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="small" />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Loading stations...</Text>
          </div>
        </div>
      );
    }

    if (availableStations.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No stations available for this event"
        />
      );
    }

    return (
      <>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          Choose a single station or average across all stations (shown as *).
        </Text>

        <Radio.Group
          value={selectedStation}
          onChange={(e) => handleStationChange(e.target.value)}
        >
          <Space direction="vertical" size={4}>
            <Radio value="avg">
              <Text style={{ fontSize: 13 }}>All stations (average)</Text>
            </Radio>
            {availableStations.map((station) => (
              <Radio key={station} value={station}>
                <Text style={{ fontSize: 13 }}>Station {station}</Text>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </>
    );
  };

  const renderFieldSelection = () => {
    if (fieldsLoading) {
      return (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="small" />
        </div>
      );
    }

    if (fieldOptions.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No parameters available for the selected station"
        />
      );
    }

    const currentField = fieldOptions.some((o) => o.value === fieldName)
      ? fieldName
      : fieldOptions[0]?.value;

    return (
      <>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          Choose which weather parameter to display on the card.
        </Text>

        <Select
          style={{ width: '100%' }}
          value={currentField}
          options={fieldOptions}
          onChange={handleFieldChange}
        />
      </>
    );
  };

  return (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Station
      </Text>

      {renderStationSelection()}

      <Divider style={{ margin: '16px 0' }} />

      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Parameter
      </Text>

      {renderFieldSelection()}
    </div>
  );
};
